// services/hospital.service.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../../../app/config";
import { axiosApiInstance } from "../../../app/axios";
import { ErrorToast, SuccessToast } from "../../../components/toast/Toasts";
import { IHospitalDetails } from "../hospitalModal";

// HOSPITAL CRUD OPERATIONS
export const getAllHospitals = createAsyncThunk(
  "Hospital/FindAllHospital",
  async (formdata: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/FindAllHospital`,
        formdata,
      );
      return {
        Data: response.data?.Data || [],
        TotalCount: response.data?.TotalCount || 0,
      };
    } catch (error: any) {
      console.error("Error Fetching Hospitals:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const createHospital = createAsyncThunk(
  "Hospital/InsertHospital",
  async (hospitalData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/InsertHospital`,
        hospitalData,
      );
      SuccessToast(response.data?.Details || "Hospital created successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error creating Hospital:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateHospital = createAsyncThunk(
  "Hospital/UpdateHospital",
  async (hospitalData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/UpdateHospital`,
        hospitalData,
      );
      SuccessToast(response.data?.Details || "Hospital updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating Hospital:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteHospital = createAsyncThunk(
  "Hospital/DeleteHospital",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/DeleteHospital`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Hospital deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error deleting Hospital:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getHospitalByID = createAsyncThunk(
  "Hospital/FindByIDHospital",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/FindByIDHospital`,
        { ID },
      );
      const result = response.data?.Data;
      return {
        ID: result?.ID,
        HospitalName: result?.HospitalName,
        HospitalTypeID: result?.HospitalTypeID,
        RegistrationNo: result?.RegistrationNo,
        Accreditations: result?.Accreditations,
        Url: result?.Url,
        TagLine: result?.TagLine,
        Address: result?.Address,
        Area: result?.Area,
        CityID: result?.CityID,
        StateID: result?.StateID,
        MobileNo: result?.MobileNo,
        EmailID: result?.EmailID,
        Website: result?.Website,
        MapURL: result?.MapURL,
        MainImage: result?.MainImage,
        RegistrationCertificateImage: result?.RegistrationCertificateImage,
        AccreditationCertificateImage: result?.AccreditationCertificateImage,
        EstablishedYear: result?.EstablishedYear,
        AboutHospital: result?.AboutHospital || "",
        TotalBed: result?.TotalBed,
        TotalDoctors: result?.TotalDoctors,
        OPDTime: result?.OPDTime,
        IsEmergencyAvailable: result?.IsEmergencyAvailable || false,
        AvailableSevice: result?.AvailableSevice
          ? result?.AvailableSevice?.split(",")?.map((item: number) => +item)
          : [],
        Specialties: result?.Specialties
          ? result?.Specialties?.split(",")?.map((item: number) => +item)
          : [],
        Facilities: result?.Facilities
          ? result?.Facilities?.split(",")?.map((item: number) => +item)
          : [],
        IsActive: result?.IsActive ? 1 : 2,
        Ratings: result?.Ratings || [],
        Images: result?.Images || [],
        AverageRating: result?.AverageRating || 0,
      } as IHospitalDetails;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching Hospital:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveHospitals = createAsyncThunk(
  "Hospital/FindAllActiveHospital",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_URL}/Hospital/FindAllActiveHospital`,
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error Fetching Active Hospitals:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const activeInactiveHospital = createAsyncThunk(
  "Hospital/ActiveInActiveHospital",
  async (formData: { ID: number; IsActive: boolean }) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/ActiveInActiveHospital`,
        formData,
      );
      SuccessToast(response.data?.Details || "Status updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating hospital status:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// HOSPITAL RATINGS
export const insertHospitalRating = createAsyncThunk(
  "Hospital/InsertHospitalRating",
  async (ratingData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/InsertHospitalRating`,
        ratingData,
      );
      SuccessToast(response.data?.Details || "Rating added successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error adding hospital rating:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateHospitalRating = createAsyncThunk(
  "Hospital/UpdateHospitalRating",
  async (ratingData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/UpdateHospitalRating`,
        ratingData,
      );
      SuccessToast(response.data?.Details || "Rating updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating hospital rating:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteHospitalRating = createAsyncThunk(
  "Hospital/DeleteHospitalRating",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/DeleteHospitalRating`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Rating deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error deleting hospital rating:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getHospitalRatingsByID = createAsyncThunk(
  "Hospital/FindByHospitalIDRating",
  async (HospitalID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/FindByHospitalIDRating`,
        { HospitalID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching hospital ratings:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// HOSPITAL IMAGES
export const uploadHospitalSingleImage = createAsyncThunk(
  "Hospital/HospitalSingleImageUpload",
  async (formData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/HospitalSingleImageUpload`,
        formData,
        {
          headers: { "content-type": "multipart/form-data" },
        },
      );
      SuccessToast(response.data?.Details || "Image uploaded successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error uploading hospital image:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const uploadHospitalMultipleImages = createAsyncThunk(
  "Hospital/HospitalMultipleImageUpload",
  async (formData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/HospitalMultipleImageUpload`,
        formData,
        {
          headers: { "content-type": "multipart/form-data" },
        },
      );
      SuccessToast(response.data?.Details || "Images uploaded successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error uploading hospital images:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteHospitalImage = createAsyncThunk(
  "Hospital/DeleteHospitalImage",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/DeleteHospitalImage`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Image deleted successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error deleting hospital image:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getHospitalImagesByID = createAsyncThunk(
  "Hospital/FindByHospitalIDImage",
  async (HospitalID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Hospital/FindByHospitalIDImage`,
        { HospitalID },
      );
      return response.data?.Data || [];
    } catch (error: any) {
      console.error("Error fetching hospital images:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);
